import { useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * QuickApplyAllCCNButton — Iter180g.
 *
 * One-tap bulk-apply of the top matched CCN preset to every priceable
 * JG on a yestimate that doesn't already have a CCN. Reuses the
 * existing per-JG matcher server-side so the same preset that would
 * appear as the leading chip on each JG is the one applied.
 *
 * Default mode is "missing" — preserves any FOH-authored CCN already
 * on a JG. The confirm dialog explicitly states this so FOH doesn't
 * worry about losing work.
 */
export default function QuickApplyAllCCNButton({ yestimateId, onApplied }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!yestimateId) return null;

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/foh/yestimate/${yestimateId}/ccn-presets/quick-apply-all`,
        { mode: "missing" },
      );
      const applied = r.data?.applied_count || 0;
      const skipped = r.data?.skipped_count || 0;
      toast.success(
        applied > 0
          ? `Applied ${applied} preset note(s)${skipped ? ` · skipped ${skipped}` : ""}`
          : "All jobs already have a customer note — nothing to apply",
      );
      onApplied?.(r.data);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quick apply failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
        data-testid="ccn-quick-apply-all-btn"
        title="Apply the top matching customer-note preset to every job that doesn't already have one"
      >
        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
        Quick Apply All Notes
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => (v ? null : setOpen(false))}>
        <AlertDialogContent
          className="max-w-md"
          data-testid="ccn-quick-apply-all-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-indigo-700">
              <Sparkles className="w-5 h-5" />
              Quick apply customer notes
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-700 text-left space-y-2">
              <p>
                We'll add the top matching customer-note preset to every
                job that doesn't already have a note.
              </p>
              <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
                <strong>Notes you've already typed are preserved.</strong>{" "}
                This only fills the gaps — it never overwrites a note
                you've authored.
              </p>
              <p className="text-xs text-slate-500">
                You can still edit any note individually after applying,
                and the AI rewrite still polishes them on each card.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={busy}
              data-testid="ccn-quick-apply-all-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={run}
              disabled={busy}
              className="bg-indigo-700 hover:bg-indigo-800 text-white"
              data-testid="ccn-quick-apply-all-confirm"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Applying…</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-1.5" />Apply to all</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
